import { FC, useMemo } from 'react'
import { Badge, Icon } from '@popety-io/ui'
import { Valuation } from '@popety-io/types'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'
import { de, fr, it, enUS } from 'date-fns/locale'
import {
  Card,
  CardHeader,
  CardTitle,
  CardActions,
  ColorDot,
  BadgeRow,
  ValueSection,
  PrimaryValue,
  RangeValue,
  CardFooter,
  ConfidenceIndicator,
  ConfidenceDot,
  ConfidenceLabel,
  DateLabel,
} from './ValuationCard.styles'
import {
  getMethodColor,
  getMethodBadgeVariant,
  getMethodI18nKey,
  getConfidenceI18nKey,
} from '../../utils'

interface ValuationCardProps {
  valuation: Valuation
  onClick: () => void
}

// Map language code to date-fns locale
const dateFnsLocales: Record<string, Locale> = {
  en: enUS,
  de: de,
  fr: fr,
  it: it,
}

export const ValuationCard: FC<ValuationCardProps> = ({
  valuation,
  onClick,
}) => {
  const { t, i18n } = useTranslation('estimate')
  const badgeVariant = getMethodBadgeVariant(valuation.method)
  const methodName = t(getMethodI18nKey(valuation.method))
  const confidenceLabel = t(getConfidenceI18nKey(valuation.confidence))
  const confidence = valuation.confidence || 2
  const color = valuation.color || getMethodColor(valuation.method)

  // Get locale for date-fns
  const locale = useMemo(
    () => dateFnsLocales[i18n.language] || enUS,
    [i18n.language]
  )

  // Format date with locale
  const updatedAgo = useMemo(() => {
    const formatted = formatDistanceToNow(valuation.updatedAt, {
      addSuffix: false,
      locale,
    })
    return formatted
  }, [valuation.updatedAt, locale])

  return (
    <Card onClick={onClick}>
      <CardHeader>
        <CardTitle>{valuation.name}</CardTitle>
        <CardActions>
          <ColorDot
            $color={color}
            title={t('manualValuations.ui.chartColor', { color })}
          />
        </CardActions>
      </CardHeader>

      <BadgeRow>
        <Badge variant={badgeVariant as any}>{methodName}</Badge>
      </BadgeRow>

      <ValueSection>
        <PrimaryValue>
          CHF {Math.round(valuation.value).toLocaleString('de-CH')}
        </PrimaryValue>
        {valuation.range && (
          <RangeValue>
            CHF {Math.round(valuation.range.min ?? 0).toLocaleString('de-CH')} -{' '}
            CHF {Math.round(valuation.range.max ?? 0).toLocaleString('de-CH')}
          </RangeValue>
        )}
      </ValueSection>

      <CardFooter>
        <ConfidenceIndicator>
          <ConfidenceDot $filled={confidence >= 1} />
          <ConfidenceDot $filled={confidence >= 2} />
          <ConfidenceDot $filled={confidence >= 3} />
          <ConfidenceLabel>{confidenceLabel}</ConfidenceLabel>
        </ConfidenceIndicator>
        <DateLabel>
          <Icon name="clock" size="small" />
          {updatedAgo}
        </DateLabel>
      </CardFooter>
    </Card>
  )
}
